import { z } from "zod";

/**
 * Verknüpfung Beauftragung ↔ Jira-Kundenabteilung (Erweiterung F1–F10 des
 * Ticket-Spiegel-Entwurfs, 2026-08-10).
 *
 * **F1 — eine aktive Kundenabteilung ist genau eine Beauftragung.** Die
 * Beauftragung trägt den Asset-Schlüssel als optionales, eindeutiges Feld; die
 * frühere Zuordnungstabelle (E3/A7) ist damit überholt und entfernt.
 *
 * **F2/F4 — bei verknüpften Beauftragungen führen die Assets die Details.**
 * Name und Service Lead kommen aus dem Asset und sind im CRM weder in der
 * Oberfläche noch über die API änderbar. Sonst überschriebe der nächtliche
 * Lauf die Eingabe, und der Fehler fiele erst am nächsten Tag auf.
 *
 * **F3 — der Service Lead am Asset entscheidet über CRM-Berechtigungen.**
 * Kette: Lead → `customer_segment_leads` → implizite Rolle `projektleiter` →
 * Sichtbarkeit. Wer in Jira einen Service Lead ändert, vergibt ab dem nächsten
 * Login Zugriff im CRM. Bewusst so entschieden, nicht als Nebenwirkung.
 *
 * **F5 — der Schlüssel ist nachträglich setzbar.** Der Bootstrap leitet ihn
 * einmalig ab; alles danach — neue Kundenabteilungen, die vier Splits — setzt
 * ein Mensch über die Oberfläche.
 */

/** Wie die Beauftragung an ihrem Asset hängt, für Anzeige und Pflege. */
export const engagementJiraAssetSchema = z.object({
  /** Assets-`objectId`, derselbe Wert wie `jira_issues.kundenabteilung_id`. */
  jiraAssetId: z.string(),
  /** Assets-`objectKey`, z. B. `KS-8793`. Fehlt, solange der Lauf ihn nicht geholt hat. */
  jiraAssetKey: z.string().nullable(),
  /** Klarname der Kundenabteilung. */
  jiraAssetLabel: z.string().nullable(),
  /** Wann Name und Service Lead zuletzt aus dem Asset übernommen wurden. */
  jiraAssetSyncedAt: z.string().nullable(),
  /** Wie viele Vorgänge im Spiegel auf diese Abteilung zeigen. */
  issueCount: z.number().int(),
});
export type EngagementJiraAsset = z.infer<typeof engagementJiraAssetSchema>;

/**
 * Eine Kundenabteilung aus dem Spiegel, wie sie zur Auswahl angeboten wird.
 *
 * `takenByEngagementName` ist gesetzt, wenn die Abteilung bereits einer
 * **anderen** Beauftragung gehört. Sie wird trotzdem angezeigt — wer sucht,
 * warum „seine" Abteilung fehlt, soll die Antwort sehen statt eine Leerstelle.
 * Übernehmen lässt sie sich nicht (F1: eindeutig).
 */
export const jiraAssetCandidateSchema = z.object({
  kundenabteilungId: z.string(),
  kundenabteilungLabel: z.string().nullable(),
  issueCount: z.number().int(),
  /**
   * Ähnlichkeit zum Kundennamen, 0…1. Die Auswahlliste sortiert danach; sie
   * ist eine Lesehilfe, keine Entscheidung — zugeordnet wird nichts von selbst.
   */
  score: z.number(),
  /** Name der Beauftragung, die die Abteilung schon hat — sonst `null`. */
  takenByEngagementName: z.string().nullable(),
});
export type JiraAssetCandidate = z.infer<typeof jiraAssetCandidateSchema>;

/** Antwort von `GET /engagements/:id/jira-asset`. */
export const jiraAssetViewSchema = z.object({
  engagementId: z.string().uuid(),
  customerName: z.string(),
  /** Die aktuell verknüpfte Kundenabteilung — `null`, solange keine hängt. */
  current: engagementJiraAssetSchema.nullable(),
  /**
   * Auswahl: Kundenabteilungen aus dem Spiegel, die ähnlichsten zuerst. Leer,
   * solange der Spiegel leer ist.
   */
  candidates: z.array(jiraAssetCandidateSchema),
  /** True, solange der Spiegel überhaupt keine Kundenabteilung kennt. */
  mirrorEmpty: z.boolean(),
});
export type JiraAssetView = z.infer<typeof jiraAssetViewSchema>;

/**
 * Body für `PUT /engagements/:id/jira-asset`.
 *
 * Übermittelt wird **nur die Kennung**; `null` löst die Verknüpfung. Schlüssel
 * und Klarname holt der Server aus dem Spiegel: Sie sind Spiegel-Daten und
 * dürfen nicht davon abhängen, was ein Client mitschickt.
 */
export const setEngagementJiraAssetSchema = z.object({
  kundenabteilungId: z.string().min(1).max(64).nullable(),
});
export type SetEngagementJiraAssetInput = z.infer<
  typeof setEngagementJiraAssetSchema
>;

/**
 * Der Service Lead steht am Asset als Jira-Benutzer und wird als
 * „Name (mail@mindsquare.de)" angezeigt. Die Auflösung auf einen CRM-
 * Mitarbeiter läuft ausschließlich über die **Mailadresse** — der Anzeigename
 * ist kein Schlüssel, und ein Namensvergleich, der stillschweigend
 * danebengreift, hat in diesem Projekt bereits Schaden angerichtet (Personio,
 * 2026-08-06). Wer sich nicht auflösen lässt, wird **nicht** gesetzt.
 *
 * Zwei Quellen, in dieser Reihenfolge:
 *   1. `user.emailAddress` am Attributwert — sauber, am 2026-08-10 an allen
 *      138 belegten Service Leads der Produktivinstanz vorhanden.
 *   2. die Klammer im Anzeigenamen — der Rückfallweg, falls die Instanz die
 *      Mailadresse aus Datenschutzgründen ausblendet (Atlassian tut das je
 *      nach Sichtbarkeitseinstellung des Kontos).
 *
 * Liefert `null`, wenn beides nichts hergibt.
 */
export function serviceLeadEmail(
  displayValue: string | null | undefined,
  userEmail: string | null | undefined,
): string | null {
  const direct = userEmail?.trim().toLowerCase();
  if (direct && direct.includes("@")) return direct;

  const match = /\(([^()\s]+@[^()\s]+)\)\s*$/.exec(displayValue ?? "");
  const fromDisplay = match?.[1]?.trim().toLowerCase();
  return fromDisplay ? fromDisplay : null;
}

/**
 * Ähnlichkeit zwischen Kundenname und Abteilungs-Klarname, 0…1.
 *
 * Steht hier statt im Backend, weil sie zum geteilten Vokabular der Zuordnung
 * gehört und ohne Datenbank testbar sein muss. Sie sortiert die Auswahlliste
 * für den Menschen, der eine Kundenabteilung nachträglich setzt (F5) — sie
 * ordnet **nichts** zu.
 *
 * Verfahren: beide Namen normalisieren (Kleinschreibung, Umlaute, Rechtsformen
 * und Satzzeichen weg), in Wörter zerlegen, Anteil der Kundenwörter zählen,
 * die auch im Abteilungsnamen vorkommen. Die Klarnamen im Spiegel sehen aus
 * wie „DENIOS SE - VS": Kundenname plus Abteilungskürzel. Ein reiner
 * Gleichheitsvergleich träfe deshalb **nie**, ein `LIKE '%name%'` dagegen auch
 * „Musterbau" bei Kunde „Bau".
 */
export function departmentNameScore(
  customerName: string,
  departmentLabel: string | null,
): number {
  if (!departmentLabel) return 0;
  const customerWords = normalizeCompanyName(customerName);
  if (customerWords.length === 0) return 0;
  const departmentWords = new Set(normalizeCompanyName(departmentLabel));
  if (departmentWords.size === 0) return 0;

  const hits = customerWords.filter((w) => departmentWords.has(w)).length;
  return hits / customerWords.length;
}

/**
 * Ab welcher Ähnlichkeit eine Abteilung als Vorschlag ganz oben steht.
 *
 * 0,5 heißt: mindestens die Hälfte der bedeutungstragenden Wörter des
 * Kundennamens steht auch im Abteilungsnamen. Bewusst nicht 1,0 — „DENIOS SE"
 * gegen „DENIOS SE - VS" träfe zwar, „Hans Kolb Wellpappe GmbH & Co. KG" gegen
 * „Hans Kolb Wellpappe GmbH & Co. KG – IT" aber schon nicht mehr zuverlässig,
 * sobald eine Schreibweise abweicht.
 */
export const DEPARTMENT_SUGGESTION_MIN_SCORE = 0.5;

/**
 * Rechtsformen und Füllwörter, die für die Ähnlichkeit nichts beitragen.
 * „GmbH" in beiden Namen ist kein Treffer, sondern Rauschen — ohne diese Liste
 * bekäme jede GmbH gegen jede GmbH einen Punkt geschenkt.
 */
const COMPANY_STOPWORDS = new Set([
  "gmbh",
  "ag",
  "se",
  "kg",
  "kgaa",
  "ohg",
  "gbr",
  "mbh",
  "co",
  "cie",
  "ug",
  "ev",
  "eg",
  "ltd",
  "plc",
  "inc",
  "llc",
  "bv",
  "nv",
  "sa",
  "sas",
  "spa",
  "srl",
  "und",
  "and",
  "der",
  "die",
  "das",
  "the",
]);

/** Kleinschreibung, Umlaute aufgelöst, Satzzeichen weg, Rechtsformen raus. */
function normalizeCompanyName(value: string): string[] {
  return value
    .toLowerCase()
    .replaceAll("ä", "ae")
    .replaceAll("ö", "oe")
    .replaceAll("ü", "ue")
    .replaceAll("ß", "ss")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter((w) => w.length > 1 && !COMPANY_STOPWORDS.has(w));
}

/**
 * Gehört ein Jira-Name (Kundenabteilung oder Kundenfirma) zu diesem Kunden?
 * Anders als `departmentNameScore` **entscheidet** diese Funktion: Der
 * TeamDaily-Eingang holt damit Vorgänge von Kunden ohne Verknüpfung
 * (2026-09-21). Deshalb streng, in zwei Richtungen:
 *
 * 1. Alle Wörter des Kundennamens stehen im Jira-Namen
 *    („Hörmann KG Verkaufsgesellschaft" in „… - HR").
 * 2. Alle Wörter des Jira-Namens **vor dem ersten „ - "** stehen im
 *    Kundennamen („ENERCON - SAP HCM" zu „ENERCON Global GmbH").
 *
 * Ein halber Treffer reicht nie: „Helmholtz-Zentrum Berlin - HR" teilt zwei
 * von drei Wörtern mit „Helmholtz-Zentrum hereon GmbH" und ist ein anderer
 * Kunde, „Gasunie Deutschland" teilt eines mit „KNDS Deutschland".
 */
export function jiraNameGehoertZuKunde(
  customerName: string,
  jiraLabel: string | null,
): boolean {
  if (!jiraLabel) return false;
  const kunde = normalizeCompanyName(customerName);
  if (kunde.length === 0) return false;
  const jira = new Set(normalizeCompanyName(jiraLabel));
  if (kunde.every((w) => jira.has(w))) return true;

  const namensteil = normalizeCompanyName(jiraLabel.split(/\s+[-–]\s+/)[0] ?? "");
  const kundenWoerter = new Set(kunde);
  return namensteil.length > 0 && namensteil.every((w) => kundenWoerter.has(w));
}
